import { AsyncForm, FormGroup, InputLabel } from '@components/async-form';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { DateInputWithTimezone, TimezoneDiff } from '@components/date-picker';
import { MeetingDuration } from '@course/components/duration-select';
import { HeadingPrimary } from '@course/components/headings';
import { useCurrentTenant } from '@components/router/session-context';
import { rpx } from 'client/lib/rpx-client';
import { useState } from 'preact/hooks';
import { FullCourse, Meeting } from 'server/types';
import { handleZoomIntegrationError } from './helpers';
import { MeetingTypeData, MeetingTypes } from './type-tab';
import { meetingUrl } from './urls';
import { Toggle } from '@components/toggle';

interface Props {
  course: Pick<FullCourse, 'id' | 'level' | 'defaultMeetingJoinUrl'>;
  onCreate: (meeting: Meeting) => void;
}

interface FormData {
  title: Meeting['title'];
  durationMinutes: number;
  scheduledAt?: Date;
  isDraft: boolean;
  holdInvites: boolean;
}

export function NewMeetingForm({ course, onCreate }: Props) {
  const { terminology } = useCurrentTenant();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    durationMinutes: 30,
    scheduledAt: undefined,
    isDraft: false,
    holdInvites: false,
  });
  const hasDefaultUrl = !!course.defaultMeetingJoinUrl;
  const [typeData, setTypeData] = useState<MeetingTypeData>({
    type: hasDefaultUrl ? 'external' : 'videoconference',
    slidesFile: undefined,
    recordMeeting: true,
    allowRecordingDownloads: true,
    zoomHasWaitingRoom: false,
    externalJoinUrl: course.defaultMeetingJoinUrl || undefined,
    requiresRsvp: false,
    rsvpEnabled: false,
  });

  async function createMeeting() {
    setIsSubmitting(true);
    try {
      const result = await rpx.meetings.createMeeting({
        courseId: course.id,
        title: formData.title,
        scheduledAt: formData.isDraft ? undefined : formData.scheduledAt?.toISOString(),
        isDraft: formData.isDraft,
        durationMinutes: formData.durationMinutes,
        type: typeData.type,
        slidesFile: typeData.slidesFile,
        recordMeeting: typeData.recordMeeting,
        allowRecordingDownloads: typeData.allowRecordingDownloads,
        zoomHasWaitingRoom: typeData.zoomHasWaitingRoom,
        // This field is an optional url.
        // So I'm sending `undefined` if it's empty
        // to skip the url validation.
        externalJoinUrl: typeData.externalJoinUrl || undefined,
        inpersonLocation: typeData.inpersonLocation || undefined,
        memberInvitesHeld: formData.holdInvites,
      });
      onCreate(result);
    } catch (err) {
      const isHandled = await handleZoomIntegrationError({
        err,
        course,
      });
      if (!isHandled) {
        throw err;
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AsyncForm class="flex flex-col py-8 w-full max-w-5xl mx-auto" onSubmit={createMeeting}>
      <HeadingPrimary title={`New ${terminology.meeting}`} />
      <FormGroup prop="title" class="mb-6">
        <InputLabel>Title</InputLabel>
        <input
          type="text"
          placeholder={`${terminology.Meeting} title`}
          name="title"
          class="inline-ruz-input w-1/2"
          onChange={(e: any) => setFormData((s) => ({ ...s, title: e.target.value }))}
        />
      </FormGroup>
      <div class="mb-6 my-2 p-4 pr-8 rounded-md border text-sm">
        <FormGroup class="" prop="scheduledAt">
          <div class={`${formData.isDraft ? 'opacity-40 pointer-events-none' : ''}`}>
            <InputLabel>Scheduled start date</InputLabel>
            <DateInputWithTimezone
              name="scheduledAt"
              includeTime
              placeholder="dd . mm . yyyy , -- : --"
              value={formData.isDraft ? undefined : formData.scheduledAt}
              disabled={formData.isDraft}
              onChange={(newDate) => {
                newDate?.setSeconds(0, 0);
                setFormData((s) => ({ ...s, scheduledAt: newDate }));
              }}
            />
            <TimezoneDiff date={formData.scheduledAt} class="mt-2" />
          </div>
        </FormGroup>
        <div class="flex items-center my-6">
          <hr class="grow dark:border-gray-700" />
          <span class="px-4 text-sm text-gray-400 dark:text-gray-500">OR</span>
          <hr class="grow dark:border-gray-700" />
        </div>

        <div class="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
          <label class="flex items-center space-x-4 cursor-pointer">
            <Toggle
              checked={formData.isDraft}
              onClick={() => setFormData((s) => ({ ...s, isDraft: !s.isDraft }))}
            />
            <div class="flex flex-col">
              <span class="font-semibold">Save as draft</span>
              <span class="text-sm text-gray-500 dark:text-gray-400">
                Save without scheduling. You can set a start time later.
              </span>
            </div>
          </label>
        </div>
        {!formData.isDraft && <TimezoneDiff date={formData.scheduledAt} class="mt-2" />}
      </div>
      <FormGroup class="mb-6" prop="durationMinutes">
        <InputLabel>Duration</InputLabel>
        <span class="text-sm">
          This is the expected duration of the {terminology.meeting}. You can always go over the
          expected time up to 4 hours per {terminology.meeting}.
        </span>
        <div class="mt-2">
          <MeetingDuration
            name="durationMinutes"
            value={formData.durationMinutes}
            onNewDuration={(newDuration) =>
              setFormData((s) => ({ ...s, durationMinutes: newDuration }))
            }
          />
        </div>
      </FormGroup>
      <div class="mb-6 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <label class="flex items-center space-x-4 cursor-pointer">
          <Toggle
            checked={formData.holdInvites}
            onClick={() => setFormData((s) => ({ ...s, holdInvites: !s.holdInvites }))}
          />
          <div class="flex flex-col">
            <span class="font-semibold">Hold calendar invites for now</span>
            <span class="text-sm text-gray-500 dark:text-gray-400">
              Don't email existing members yet. The host will still get a calendar invite. Release
              with the "Send invites now" button on the {terminology.meeting} detail page once
              you're done scheduling.
            </span>
          </div>
        </label>
      </div>
      {hasDefaultUrl ? (
        <FormGroup class="mb-6" prop="type">
          <MeetingTypes
            meeting={typeData}
            course={course}
            canEditDetails
            onChange={(updates) => setTypeData({ ...typeData, ...updates })}
            onSlideDelete={() => setTypeData({ ...typeData, slidesFile: undefined })}
          />
        </FormGroup>
      ) : (
        <FormGroup class="mb-6" prop="type">
          <InputLabel>Choose the {terminology.meeting} type</InputLabel>
          <span class="text-sm">
            You may update the {terminology.meeting} details and upload presentations later.
          </span>
          <MeetingTypes
            meeting={typeData}
            course={course}
            canEditDetails
            onChange={(updates) => setTypeData({ ...typeData, ...updates })}
            onSlideDelete={() => setTypeData({ ...typeData, slidesFile: undefined })}
          />
        </FormGroup>
      )}
      <footer class="flex mt-2 justify-end">
        <BtnPrimary class="px-8 py-3" isLoading={isSubmitting}>
          Create {terminology.Meeting}
        </BtnPrimary>
        <BtnSecondary
          type="button"
          class="ml-4 px-8 py-3"
          href={meetingUrl({ courseId: course.id })}
        >
          Cancel
        </BtnSecondary>
      </footer>
    </AsyncForm>
  );
}
